import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import toast from 'react-hot-toast';

import {
    handleFetchErrors,
    onCompletedHandler,
    onErrorHandler,
} from '@/services/apollo';
import { handleColumnSort, SortDirection } from '@/services/sorting';
import routes from '@/services/routes';
import ellipsisWrapper from '@/services/ellipsisWrapper';
import Loader from '@/components/commons/loader';
import EmptyState from '@/components/commons/empty_state';
import ToggleForm from '@/components/forms/toggle_form';
import Button, { ButtonSize } from '@/components/commons/button';
import Plus from '@/components/commons/icons/Plus';
import SmartTable from '@/components/commons/table/SmartTable';
import CheckMark from '@/components/commons/icons/CheckMark';
import CloseButton from '@/components/commons/icons/CloseButton';
import useResize from '@/hooks/useResize';
import TrashBin from '@/components/commons/icons/TrashBin';
import PenOutline from '@/components/commons/icons/PenOutline';
import ConfirmActionModal from '@/components/settings/ConfirmActionModal';

import { emailDomainsState } from './State';
import { FETCH_EMAIL_DOMAINS, DELETE_EMAIL_DOMAIN, UPDATE_EMAIL_DOMAIN } from './Actions';
import styles from './EmailDomains.module.css';

export const DEFAULT_SORT_COLUMN = 'domain';
export const DEFAULT_NAME_SORT_DIRECTION = SortDirection.ASC;

function EmailDomainsTable({ setLoading, setFormError, formError }) {
    const [emailDomains, setEmailDomains] = useRecoilState(emailDomainsState);
    const [isError, setIsError] = useState(false);
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_NAME_SORT_DIRECTION);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedRecordLabel, setSelectedRecordLabel] = useState('');
    const [modalState, setModalState] = useState({ show: false, action: null });
    const [activeRecord, setActiveRecord] = useState(null);

    const router = useRouter();
    const [width] = useResize();

    const selectRecord = (record) => {
        setSelectedRecord(record.id);
        setSelectedRecordLabel(record.domain);
    };

    const {
        loading, refetch,
    } = useQuery(FETCH_EMAIL_DOMAINS, {
        variables: {
            id: null,
            sortColumn,
            sortDirection,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const emailDomainsData = data.user.emailDomains.map((emailDomain) => ({
                id: emailDomain.id,
                domain: emailDomain.domain,
                enabled: emailDomain.enabled,
            }));
            setEmailDomains(emailDomainsData);
            setLoading(false);
        }),
    });

    const [deleteEmailDomain] = useMutation(DELETE_EMAIL_DOMAIN);
    const handleDeleteEmailDomain = async (id) => {
        try {
            setLoading(true);
            const response = await deleteEmailDomain({
                variables: { deleteEmailDomainInput: { id } },
            });
            refetch();

            if (!response.errors) {
                if (formError !== '') setFormError('');
                toast.success('Domain removed successfully.');
            }
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const openDeleteDomainModal = async (record) => {
        setModalState({ show: true, action: null });
        setActiveRecord(record);
    };

    const confirmDeleteDomain = async () => {
        handleDeleteEmailDomain(activeRecord);
        setActiveRecord(null);
        setModalState({ show: false, action: null });
    };

    const [updateEmailDomain] = useMutation(UPDATE_EMAIL_DOMAIN);
    const handleUpdateEmailDomainEnabled = async (id, enabled, _) => {
        try {
            setLoading(true);
            const response = await updateEmailDomain({
                variables: {
                    updateEmailDomainInput: {
                        id,
                        attributes: {
                            enabled,
                        },
                    },
                },
            });

            if (!response.data.updateEmailDomain.emailDomain.errors) {
                refetch();
                if (formError !== '') setFormError('');
                toast.success('Known Domain was updated successfully.');
                return;
            }

            setFormError('Something went wrong.  Please try again.');
            setLoading(false);
        } catch (err) {
            setLoading(false);
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const handleUpdateEmailDomainName = async () => {
        try {
            setLoading(true);
            const response = await updateEmailDomain({
                variables: {
                    updateEmailDomainInput: {
                        id: selectedRecord,
                        attributes: {
                            domain: selectedRecordLabel,
                        },
                    },
                },
            });

            if (!response.data.updateEmailDomain.emailDomain.errors) {
                setSelectedRecord(null);
                refetch();
                if (formError !== '') setFormError('');
                toast.success('Known Domain was updated successfully.');
                return;
            }

            setFormError('Something went wrong.  Please try again.');
            setLoading(false);
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
            setLoading(false);
        }
    };

    const onSort = handleColumnSort(sortColumn, setSortColumn, sortDirection, setSortDirection);

    const domainColumn = {
        label: 'Domain',
        id: 'domain',
        onSort: () => onSort('domain', DEFAULT_NAME_SORT_DIRECTION),
        render: (rowId, record) => {
            if (record.id === selectedRecord) {
                return (
                    <input
                        type="text"
                        name="label"
                        id={`${rowId}-${record.id}`}
                        value={selectedRecordLabel}
                        className={styles.nameField}
                        onChange={(e) => setSelectedRecordLabel(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Enter') {
                                handleUpdateEmailDomainName();
                                e.preventDefault();
                            }
                        }}
                    />
                );
            }
            return <div key={rowId} className="text-base break-all">{ellipsisWrapper(record.domain, Math.floor(width * 0.06))}</div>;
        },
    };

    const editColumn = {
        label: '',
        id: 'editAction',
        shrink: false,
        render: (rowId, record) => (
            <div key={rowId} className="flex items-center">
                {selectedRecord === record.id ? (
                    <div className={styles.actionsWrapper}>
                        <button
                            type="button"
                            data-tooltip-id="tip"
                            data-tooltip-content="Save"
                            title="Save"
                            aria-label="Save"
                            className="text-progressBarBlue"
                            onClick={handleUpdateEmailDomainName}
                        >
                            <CheckMark />
                        </button>
                        <button
                            type="button"
                            data-tooltip-id="tip"
                            data-tooltip-content="Cancel"
                            title="Cancel"
                            aria-label="Cancel"
                            className="text-progressBarBlue"
                            onClick={() => setSelectedRecord(null)}
                        >
                            <CloseButton />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        data-tooltip-id="tip"
                        data-tooltip-content="Edit this Domain"
                        title="Edit this Domain"
                        aria-label="Edit this Domain"
                        className={`${styles.actionButton} linkLike--secondary`}
                        onClick={() => selectRecord(record)}
                    >
                        <PenOutline className="w-4 h-4 m-auto" />
                    </button>
                )}
            </div>
        ),
    };

    const columns = [
        domainColumn,
        {
            label: 'Enabled',
            id: 'enabled',
            render: (rowId, record) => {
                const fieldName = `enabled-${record.id}`;
                const initialValues = {};
                initialValues[fieldName] = record.enabled;

                return (
                    <div key={rowId} className="ml-1">
                        <Formik
                            onSubmit={() => { }}
                            initialValues={initialValues}
                        >
                            <ToggleForm
                                record={record}
                                onChange={handleUpdateEmailDomainEnabled}
                                fieldName={fieldName}
                                propertyName="enabled"
                            />
                        </Formik>
                    </div>
                );
            },
        },
        editColumn,
        {
            label: '',
            id: 'deleteAction',
            render: (rowId, record) => (
                <div key={rowId} className="flex items-center">
                    <button
                        type="button"
                        data-tooltip-id="tip"
                        data-tooltip-content="Delete this Domain"
                        title="Delete this Domain"
                        aria-label="Delete this Domain"
                        className={`${styles.actionButton} linkLike--secondary`}
                        onClick={() => openDeleteDomainModal(record.id)}
                    >
                        <TrashBin className="w-4 h-4 m-auto" />
                    </button>
                </div>
            ),
        },
    ];

    const tableTitle = () => (
        <div className="flex gap-2 items-center justify-between w-full flex-wrap">
            <p>
                <b>Domains</b>
                {` (${emailDomains?.length})`}
            </p>
            <Button
                size={ButtonSize.base}
                text="Add domain"
                onClick={() => router.push(routes.ADD_EMAIL_DOMAINS)}
                icon={<Plus className="mr-2" />}
            />
        </div>
    );

    const externalStyles = {
        table: 'min-w-[300px]',
        domain: 'min-w-[200px] w-[100%]',
        enabled: 'w-[100%]',
        editAction: 'w-[100%]',
        deleteAction: 'w-[100%]',
    };

    return (
        <Loader isLoading={loading && !emailDomains} isError={isError}>
            <SmartTable
                externalStyles={externalStyles}
                columns={columns}
                data={emailDomains}
                title={tableTitle}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                paginationState={{ windowSize: 3, initialPage: 0 }}
            />
            <Tooltip id="tip" />
            {emailDomains && emailDomains.length < 1
                && (
                    <EmptyState
                        callToActionMessage="You haven't added any Domains yet. Please click on the plus icon above to add one!"
                        pageName="Domains"
                    />
                )}
            <ConfirmActionModal
                modalType="agenda"
                element="Domain"
                display={modalState.show}
                onSubmit={() => confirmDeleteDomain()}
                closeModal={() => {
                    setActiveRecord(null);
                    setModalState({ show: false, action: null });
                }}
                actionType={modalState.action || ''}
            />
        </Loader>
    );
}

export default EmailDomainsTable;
